import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { fpBase, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const AREAS = ["revenue", "pvc", "dvc", "fixed_cost", "depreciation", "interest", "tax",
  "working_capital", "cash", "fixed_assets", "debt", "equity"];
const AREA_LABEL = {
  revenue: "Revenue", pvc: "Production variable cost", dvc: "Distribution variable cost",
  fixed_cost: "Fixed cost", depreciation: "Depreciation", interest: "Interest", tax: "Tax",
  working_capital: "Working capital", cash: "Cash", fixed_assets: "Fixed assets",
  debt: "Debt", equity: "Equity",
};
const METHODS = [["direct_accounting", "Direct accounting"], ["direct_tag", "Direct tag"],
  ["interface_allocation", "Interface allocation"], ["direct_source", "Direct source"]];

const blank = {
  source_code: "TB", source_field: "Ledger Name", source_value: "", destination_area: "revenue",
  destination_dimension: "", destination_value: "", control_item: "",
  allocation_method: "direct_accounting", effective_from: "", notes: "", status: "approved",
};

const MasterEditor = ({ tenantId, masters, reload }) => {
  const [drafts, setDrafts] = useState({});
  useEffect(() => {
    setDrafts(Object.fromEntries(Object.entries(masters).map(([k, v]) => [k, (v.items || []).join("\n")])));
  }, [masters]);

  const save = async (kind) => {
    try {
      await api.put(`${fpBase(tenantId)}/masters/${kind}`, {
        items: (drafts[kind] || "").split("\n").map((s) => s.trim()).filter(Boolean) });
      toast.success(`${masters[kind].label} saved as a new version`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="fp-masters">
      {Object.entries(masters).map(([kind, m]) => (
        <div key={kind} className="panel p-5" data-testid={`fp-master-${kind}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-navy">{m.label}</p>
            <span className="label-caps">v{m.version}</span>
          </div>
          <textarea rows={6} value={drafts[kind] || ""} data-testid={`fp-master-input-${kind}`}
                    onChange={(e) => setDrafts({ ...drafts, [kind]: e.target.value })}
                    className="mt-2 w-full resize-y rounded-xl border border-border bg-card px-3 py-2 font-mono text-xs outline-none focus:border-terracotta" />
          <button onClick={() => save(kind)} data-testid={`fp-master-save-${kind}`}
                  className="mt-2 rounded-full bg-navy px-4 py-2 text-[10px] uppercase tracking-[0.12em] text-white hover:bg-navy/90">
            Save version
          </button>
        </div>
      ))}
    </div>
  );
};

export default function FpMapping() {
  const { tenantId } = useFpContext();
  const [rows, setRows] = useState([]);
  const [masters, setMasters] = useState(null);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(blank);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [m, ms, s] = await Promise.all([
        api.get(`${fpBase(tenantId)}/mappings`),
        api.get(`${fpBase(tenantId)}/masters`),
        api.get(`${fpBase(tenantId)}/sources`),
      ]);
      setRows(m.data.filter((r) => r.status !== "retired"));
      setMasters(ms.data);
      setSources(s.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post(`${fpBase(tenantId)}/mappings`, {
        ...form, source_value: form.source_value.trim() || "*" });
      toast.success("Mapping published as a new version");
      setForm(blank);
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const retire = async (id) => {
    try {
      await api.delete(`${fpBase(tenantId)}/mappings/${id}`);
      toast.success("Mapping retired (history preserved)");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <FpShell showPeriod={false}
      title="Mapping master & dimension masters"
      description="Aikyam maps each client's own ledgers, dimensions and interface values onto the standard financial model — no code change required. Every edit creates a new version; retired rows are kept."
      actions={
        <button onClick={() => setOpen((v) => !v)} data-testid="fp-mapping-add-toggle"
                className="rounded-full bg-terracotta px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
          {open ? "Close" : "Add / update mapping"}
        </button>
      }
    >
      <div className="space-y-6">
        {open && (
          <div className="panel p-6" data-testid="fp-mapping-form">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {[
                ["source_code", "Source", "select", sources.map((s) => [s.source_code, s.label])],
                ["source_field", "Source field / ledger", "text"],
                ["source_value", "Source value (blank = all rows)", "text"],
                ["destination_area", "Destination area", "select", AREAS.map((a) => [a, AREA_LABEL[a]])],
                ["destination_dimension", "Destination dimension", "text"],
                ["destination_value", "Destination value", "text"],
                ["control_item", "Control item (blank = no control)", "text"],
                ["allocation_method", "Allocation method", "select", METHODS],
                ["effective_from", "Effective from", "date"],
              ].map(([key, label, kind, options]) => (
                <label key={key} className="block">
                  <span className="label-caps">{label}</span>
                  {kind === "select" ? (
                    <select value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                            data-testid={`fp-mapping-${key}`}
                            className="mt-1.5 w-full rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta">
                      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  ) : (
                    <input type={kind} value={form[key]} data-testid={`fp-mapping-${key}`}
                           onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                           className="mt-1.5 w-full rounded-xl border border-border bg-card px-3 py-2 text-xs text-navy outline-none focus:border-terracotta" />
                  )}
                </label>
              ))}
            </div>
            <button onClick={submit} data-testid="fp-mapping-save"
                    className="mt-5 rounded-full bg-navy px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-navy/90">
              Publish mapping
            </button>
          </div>
        )}

        <div className="panel overflow-hidden" data-testid="fp-mapping-table">
          <div className="border-b border-border px-6 py-4">
            <p className="label-caps">Mapping master · {rows.length} active rows</p>
            <h3 className="font-display text-lg text-navy">Source → standard financial model</h3>
          </div>
          <div className="max-h-[32rem] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Source</th>
                  <th className="px-4 py-3 text-left">Source value</th>
                  <th className="px-4 py-3 text-left">Destination area</th>
                  <th className="px-4 py-3 text-left">Dimension</th>
                  <th className="px-4 py-3 text-left">Control item</th>
                  <th className="px-4 py-3 text-left">Method</th>
                  <th className="px-4 py-3 text-right">Version</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`fp-mapping-row-${r.id}`}>
                    <td className="px-4 py-2.5 text-navy">{r.source_code}</td>
                    <td className="px-4 py-2.5 font-medium text-navy">{r.source_value}</td>
                    <td className="px-4 py-2.5 text-foreground/80">{AREA_LABEL[r.destination_area] || r.destination_area}</td>
                    <td className="px-4 py-2.5 text-foreground/70">{r.destination_dimension || "—"}</td>
                    <td className="px-4 py-2.5 text-foreground/80">{r.control_item || "—"}</td>
                    <td className="px-4 py-2.5 text-foreground/70">{String(r.allocation_method || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums">{r.version}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => retire(r.id)} data-testid={`fp-mapping-retire-${r.id}`}
                              className="rounded-full border border-border px-3 py-1 text-[9px] uppercase tracking-[0.1em] text-navy hover:bg-muted">
                        Retire
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {masters && <MasterEditor tenantId={tenantId} masters={masters} reload={load} />}
      </div>
    </FpShell>
  );
}
